/* Active-market data: countries, cities, districts.
 *
 * NO-HARDCODE LAW. Nothing about which markets exist, which are open, what
 * they cost in, or what their phone codes are is written in this codebase.
 * All of it is read from the backend:
 *
 *   - Countries render only where is_active is true.
 *   - The country picker is not a design decision, it is a derived one:
 *     `showCountryPicker` is simply activeCountries.length > 1. With a single
 *     active market no picker appears anywhere in the UI; the moment the
 *     backend flips a second country to active, one materialises. No code
 *     change, no deploy.
 *   - Currency and phone code come from the active country row, never from a
 *     constant. There is no "EGP" literal in this file.
 */

import { apiGet } from "./client";

export type CountryRow = {
  id: number;
  name_en: string;
  name_ar: string;
  iso_code: string | null;
  phone_code: string | null;
  currency: string | null;
  is_active: boolean;
};

export type CityRow = {
  id: number;
  country_id: number | null;
  name_en: string;
  name_ar: string;
  is_active: boolean;
};

export type DistrictRow = {
  id: number;
  city_id: number | null;
  name_en: string;
  name_ar: string;
  /* Migration 012, and normally null: a short name exists only where the full
   * one will not fit a home area tile. Optional on the row because a worker
   * older than v3.8.12 will not return these columns. */
  short_name_ar?: string | null;
  short_name_en?: string | null;
  is_active: boolean;
};

export type Country = {
  id: number;
  nameAr: string;
  nameEn: string;
  iso: string;
  phoneCode: string | null;
  currency: string | null;
};

export type City = { id: number; countryId: number | null; nameAr: string; nameEn: string };
export type District = {
  id: number; cityId: number | null; nameAr: string; nameEn: string;
  /** Empty string when unset — the caller falls back to the full name. */
  shortNameAr: string; shortNameEn: string;
};

export type MarketData = {
  /** Active countries only — inactive rows are dropped, never rendered. */
  countries: Country[];
  cities: City[];
  districts: District[];
  /** Derived, never configured. */
  showCountryPicker: boolean;
  /** The single active market, when there is exactly one. */
  soleCountry: Country | null;
};

function activeCountries(rows: CountryRow[]): Country[] {
  return rows
    .filter((r) => r.is_active)
    .sort((a, b) => a.id - b.id)
    .map((r) => ({
      id: r.id,
      nameAr: r.name_ar ?? "",
      nameEn: r.name_en ?? "",
      iso: (r.iso_code ?? "").toUpperCase(),
      phoneCode: r.phone_code ?? null,
      currency: r.currency ?? null,
    }));
}

export function buildMarketData(
  countryRows: CountryRow[],
  cityRows: CityRow[],
  districtRows: DistrictRow[],
): MarketData {
  const countries = activeCountries(countryRows);
  const openIds = new Set(countries.map((c) => c.id));

  // A city in a closed country must not surface, even if the city row itself
  // is active — the country's is_active flag is the outer gate.
  const cities = cityRows
    .filter((r) => r.is_active && r.country_id != null && openIds.has(r.country_id))
    .sort((a, b) => a.id - b.id)
    .map((r) => ({
      id: r.id,
      countryId: r.country_id,
      nameAr: r.name_ar ?? "",
      nameEn: r.name_en ?? "",
    }));

  const openCityIds = new Set(cities.map((c) => c.id));
  const districts = districtRows
    .filter((r) => r.is_active && r.city_id != null && openCityIds.has(r.city_id))
    .sort((a, b) => a.id - b.id)
    .map((r) => ({
      id: r.id,
      cityId: r.city_id,
      nameAr: r.name_ar ?? "",
      nameEn: r.name_en ?? "",
      shortNameAr: r.short_name_ar ?? "",
      shortNameEn: r.short_name_en ?? "",
    }));

  return {
    countries,
    cities,
    districts,
    showCountryPicker: countries.length > 1,
    soleCountry: countries.length === 1 ? countries[0] : null,
  };
}

export async function fetchMarketData(signal?: AbortSignal): Promise<MarketData> {
  const [countryRows, cityRows, districtRows] = await Promise.all([
    apiGet<CountryRow>("countries", signal),
    apiGet<CityRow>("cities", signal),
    apiGet<DistrictRow>("districts", signal),
  ]);
  return buildMarketData(countryRows, cityRows, districtRows);
}

/* ── Derived helpers — all read from market data, none hardcoded ─────── */

export function citiesForCountry(data: MarketData, countryId: number | null): City[] {
  if (countryId == null) return data.cities;
  return data.cities.filter((c) => c.countryId === countryId);
}

export function districtsForCity(data: MarketData, cityId: number | null): District[] {
  if (cityId == null) return [];
  return data.districts.filter((d) => d.cityId === cityId);
}

/** Currency for a market, straight off the country row. */
export function currencyFor(data: MarketData, countryId: number | null): string | null {
  const country =
    countryId != null ? data.countries.find((c) => c.id === countryId) : data.soleCountry;
  return country?.currency ?? null;
}

/** Phone codes for every open market — the input prefix set. */
export function phoneCodes(data: MarketData): string[] {
  return data.countries
    .map((c) => c.phoneCode)
    .filter((p): p is string => !!p);
}

/* ── Localised place names ────────────────────────────────────────────────
 * Listing rows carry `city_text` / `district_text`, but those are English
 * strings. Rendering them directly would put Latin words inside the Arabic
 * UI and break locale purity, so names are resolved from the reference tables
 * by id, which carry both name_ar and name_en.
 *
 * The English text column is used only as a last resort, when a listing
 * references a place id that is missing or inactive in the reference data.
 * That case is a backend data gap rather than a display choice. */

export function cityName(
  data: MarketData | null, cityId: number | null, fallback: string | null, ar: boolean,
): string | null {
  const row = data && cityId != null ? data.cities.find((c) => c.id === cityId) : undefined;
  if (row) return ar ? row.nameAr || row.nameEn : row.nameEn || row.nameAr;
  return fallback;
}

export function districtName(
  data: MarketData | null, districtId: number | null, fallback: string | null, ar: boolean,
): string | null {
  const row = data && districtId != null ? data.districts.find((d) => d.id === districtId) : undefined;
  if (row) return ar ? row.nameAr || row.nameEn : row.nameEn || row.nameAr;
  return fallback;
}

/* Raw rows, unfiltered — for the admin markets screen only.
 *
 * The public surface must never see a closed market, which is why
 * buildMarketData drops inactive rows. The console has the opposite need: an
 * admin has to see the disabled countries and cities precisely because those
 * are the ones they might reopen. The proxy's public GET returns every row
 * (it filters no `is_active`), so this needs no admin route.
 */
export async function fetchMarketRows(signal?: AbortSignal): Promise<{
  countries: CountryRow[]; cities: CityRow[]; districts: DistrictRow[];
}> {
  const [countries, cities, districts] = await Promise.all([
    apiGet<CountryRow>("countries?order=id.asc", signal),
    apiGet<CityRow>("cities?order=id.asc", signal),
    apiGet<DistrictRow>("districts?order=id.asc", signal),
  ]);
  return { countries, cities, districts };
}
